import React, { useRef, useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText as GSAPSplitText } from "gsap/SplitText";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(ScrollTrigger, GSAPSplitText, useGSAP);

export interface SplitTextProps {
  text: string;
  className?: string;
  delay?: number;
  duration?: number;
  ease?: string | ((t: number) => number);
  splitType?: "chars" | "words" | "lines" | "words, chars";
  from?: gsap.TweenVars;
  to?: gsap.TweenVars;
  threshold?: number;
  rootMargin?: string;
  tag?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span";
  textAlign?: React.CSSProperties["textAlign"];
  onLetterAnimationComplete?: () => void;

  // ✅ added to support `<SplitText showCallback />`
  showCallback?: boolean;
}

const SplitText: React.FC<SplitTextProps> = ({
  text,
  className = "",
  delay = 50,
  duration = 1.25,
  ease = "power3.out",
  splitType = "chars",
  from = { opacity: 0, y: 40 },
  to = { opacity: 1, y: 0 },
  threshold = 0.1,
  rootMargin = "-100px",
  textAlign = "center",
  tag = "p",
  onLetterAnimationComplete,
  showCallback = false
}) => {
  const ref = useRef<HTMLElement | null>(null);
  const animationCompletedRef = useRef(false);
  const onCompleteRef = useRef(onLetterAnimationComplete);
  const [fontsLoaded, setFontsLoaded] = useState<boolean>(false);

  // Keep callback ref updated
  useEffect(() => {
    onCompleteRef.current = onLetterAnimationComplete;
  }, [onLetterAnimationComplete]);

  useEffect(() => {
    if (document.fonts?.status === "loaded") {
      setFontsLoaded(true);
    } else {
      document.fonts?.ready.then(() => setFontsLoaded(true));
    }
  }, []);

  useGSAP(
    () => {
      if (!ref.current || !text || !fontsLoaded) return;
      if (animationCompletedRef.current) return;

      const el = ref.current as HTMLElement & { _rbsplitInstance?: GSAPSplitText };

      if (el._rbsplitInstance) {
        try {
          el._rbsplitInstance.revert();
        } catch (_) {}
        el._rbsplitInstance = undefined;
      }

      const startPct = (1 - threshold) * 100;

      const marginMatch = /^(-?\d+(?:\.\d+)?)(px|em|rem|%)?$/.exec(rootMargin);
      const marginValue = marginMatch ? parseFloat(marginMatch[1]) : 0;
      const marginUnit = marginMatch ? marginMatch[2] || "px" : "px";
      const sign =
        marginValue === 0
          ? ""
          : marginValue < 0
            ? `-=${Math.abs(marginValue)}${marginUnit}`
            : `+=${marginValue}${marginUnit}`;

      const start = `top ${startPct}%${sign}`;

      let targets: Element[] = [];
      const assignTargets = (self: GSAPSplitText) => {
        if (splitType.includes("chars") && self.chars.length) targets = self.chars;
        if (!targets.length && splitType.includes("words") && self.words.length) targets = self.words;
        if (!targets.length && splitType.includes("lines") && self.lines.length) targets = self.lines;
        if (!targets.length) targets = self.chars || self.words || self.lines;
      };

      const splitInstance = new GSAPSplitText(el, {
        type: splitType,
        smartWrap: true,
        autoSplit: splitType === "lines",
        linesClass: "split-line",
        wordsClass: "split-word",
        charsClass: "split-char",
        reduceWhiteSpace: false,
        onSplit: (self: GSAPSplitText) => {
            assignTargets(self);
          
            const tweenVars: gsap.TweenVars = {
              ...to,
              duration,
              ease,
              stagger: delay / 1000,
              immediateRender: false,
              onComplete: () => {
                animationCompletedRef.current = true;
                if (showCallback) onCompleteRef.current?.();
              }
            };
          
            // ✅ If element is fixed OR already visible -> animate immediately
            const computed = window.getComputedStyle(el);
            const isFixed = computed.position === "fixed";
          
            const rect = el.getBoundingClientRect();
            const inView =
              rect.bottom > 0 &&
              rect.top < (window.innerHeight || document.documentElement.clientHeight);
          
            if (isFixed || inView) {
              return gsap.fromTo(targets, { ...from }, tweenVars);
            }
          
            // Otherwise: animate on scroll
            return gsap.fromTo(targets, { ...from }, {
              ...tweenVars,
              scrollTrigger: {
                trigger: el,
                start: start,
                once: true
              }
            });
          }          
      });

      el._rbsplitInstance = splitInstance;

      return () => {
        ScrollTrigger.getAll().forEach((st) => {
          if (st.trigger === el) st.kill();
        });
        try {
          splitInstance.revert();
        } catch (_) {}
        el._rbsplitInstance = undefined;
      };
    },
    {
      dependencies: [
        text,
        delay,
        duration,
        ease,
        splitType,
        JSON.stringify(from),
        JSON.stringify(to),
        threshold,
        rootMargin,
        fontsLoaded,
        showCallback
      ],
      scope: ref
    }
  );

  const style: React.CSSProperties = {
    textAlign,
    overflow: "hidden",
    display: "inline-block",
    whiteSpace: "normal",
    wordWrap: "break-word",
    willChange: "transform, opacity"
  };

  const classes = `split-parent ${className}`;

  switch (tag) {
    case "h1":
      return (
        <h1 ref={ref as any} style={style} className={classes}>
          {text}
        </h1>
      );
    case "h2":
      return (
        <h2 ref={ref as any} style={style} className={classes}>
          {text}
        </h2>
      );
    case "h3":
      return (
        <h3 ref={ref as any} style={style} className={classes}>
          {text}
        </h3>
      );
    case "h4":
      return (
        <h4 ref={ref as any} style={style} className={classes}>
          {text}
        </h4>
      );
    case "h5":
      return (
        <h5 ref={ref as any} style={style} className={classes}>
          {text}
        </h5>
      );
    case "h6":
      return (
        <h6 ref={ref as any} style={style} className={classes}>
          {text}
        </h6>
      );
    case "span":
      return (
        <span ref={ref as any} style={style} className={classes}>
          {text}
        </span>
      );
    default:
      return (
        <p ref={ref as any} style={style} className={classes}>
          {text}
        </p>
      );
  }
};

export default SplitText;